import { useMutation } from '@apollo/client'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Button,
  ButtonColor,
  NotificationType,
  TextArea,
  FileUpload,
  PdfZoom,
  Icon,
  IconType,
  DownloadFileButton,
} from '../../../components'
import {
  UPDATE_BRAND,
  UPDATE_CAMPAIGN,
  GET_REQUIREMENTS_UPLOAD_URL,
} from '../../../gql'
import { Brand, BrandOnboardingStep, Campaign } from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import { Bullets } from '../components/Bullets'
import { Steps } from '../components/Steps'
import { useNotification } from '../../../utils/hooks'
import { extractUuidFromFileName } from '../../../utils/helpers'

type EditFormData = {
  requirements: string
  requirementsName: string
  requirementsMime: string
}

interface Props {
  brand: Brand
  campaign: Campaign
}

const SetRequirements = ({ brand, campaign }: Props) => {
  const { addNotification } = useNotification()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [tempFileUrl, setTempFileUrl] = useState<string | null>(null)

  const [updateBrand, { loading: loadingUpdateBrand }] =
    useMutation(UPDATE_BRAND)
  const [updateCampaign, { loading: loadingUpdateCampaign }] =
    useMutation(UPDATE_CAMPAIGN)
  const [getRequirementsUploadUrl] = useMutation(GET_REQUIREMENTS_UPLOAD_URL)

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<EditFormData>({
    defaultValues: {
      requirements: campaign?.requirements,
      requirementsName: campaign?.requirementsName,
      requirementsMime: campaign?.requirementsMime,
    },
  })

  useEffect(() => {
    if (campaign) {
      reset({
        requirements: campaign.requirements,
        requirementsName: campaign.requirementsName,
        requirementsMime: campaign.requirementsMime,
      })
    }
  }, [campaign, reset])

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewBrandsOnboardingSetRequirements)
  }, [])

  // Create object URL for the selected file
  useEffect(() => {
    // Clean up previous URL
    if (tempFileUrl) {
      URL.revokeObjectURL(tempFileUrl)
    }

    // Create new URL for selected file
    if (selectedFile) {
      const newUrl = URL.createObjectURL(selectedFile)
      setTempFileUrl(newUrl)
    }

    if (!selectedFile) {
      setTempFileUrl(null)
    }

    // Clean up on component unmount
    return () => {
      if (tempFileUrl) {
        URL.revokeObjectURL(tempFileUrl)
      }
    }
  }, [selectedFile])

  const goBack = async () => {
    try {
      await updateBrand({
        variables: {
          id: brand.id,
          input: {
            onboarding: BrandOnboardingStep.setVideoPlacements,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  const handleFileChange = async (file: File) => {
    setSelectedFile(file)
  }

  const uploadFile = async () => {
    if (!selectedFile) return null

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const mime = selectedFile.type

      const { data } = await getRequirementsUploadUrl({
        variables: {
          campaignId: campaign.id,
          mime,
        },
      })

      const { uploadUrl, fileName } = data.getRequirementsUploadUrl

      // push content to S3 with progress tracking
      await axios.put(uploadUrl, selectedFile, {
        headers: {
          'Content-Type': mime,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
          setUploadProgress(percentCompleted)
        },
      })

      return { fileName, mime }
    } catch (error) {
      console.error('Error uploading file:', error)
      addNotification(
        'Something went wrong uploading the file. Please try again.',
        NotificationType.error,
      )
      return null
    } finally {
      setIsUploading(false)
    }
  }

  const onSubmit = async ({ requirements }: EditFormData) => {
    try {
      let fileData = null

      if (selectedFile) {
        fileData = await uploadFile()

        if (!fileData) {
          return
        }
      }

      await Promise.all([
        updateCampaign({
          variables: {
            id: campaign.id,
            input: {
              requirements,
              requirementsName: fileData
                ? fileData.fileName
                : campaign.requirementsName,
              requirementsMime: fileData
                ? fileData.mime
                : campaign.requirementsMime,
            },
          },
        }),
        updateBrand({
          variables: {
            id: brand.id,
            input: {
              onboarding: BrandOnboardingStep.setAddons,
            },
          },
        }),
      ])

      setSelectedFile(null)
      setTempFileUrl(null)
    } catch (error) {
      console.log('@error SetRequirements', error.message)
      if (error.message) {
        setError('requirements', {
          type: 'custom',
          message: error.message,
        })
        return
      }

      addNotification(error.message, NotificationType.error)
    }
  }

  // Check if there's a PDF to display
  const isPdfMimeType = campaign?.requirementsMime?.includes('pdf')
  const hasPdfName = !!campaign?.requirementsName
  const hasPdfToShow = isPdfMimeType && hasPdfName

  // Check PDF URL
  const pdfUrl = campaign?.requirementsFile?.url

  return (
    <div className='min-h-screen flex flex-col pb-14'>
      <div className='flex-grow'>
        <Steps
          brand={brand}
          stepCurrent={BrandOnboardingStep.setRequirements}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='md:shadow rounded-t-md sm:overflow-hidden'>
                <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                  <div className='sm:mx-auto'>
                    <h2 className='mt-0 text-3xl font-extrabold text-gray-900'>
                      Mentions & Scenes
                    </h2>
                    <p className='mt-2 text-sm text-gray-600'>
                      Consider mentioning campaign goals, specific scenes, or
                      other essential aspects for your marketing video.
                    </p>
                  </div>

                  <div className='grid gap-6'>
                    <div className='col-span-3 sm:col-span-2'>
                      <TextArea
                        error={errors.requirements?.message}
                        label='Talking points'
                        placeholder='Mentions, scenes or other requirements'
                        {...register('requirements', {
                          maxLength: {
                            value: 10000,
                            message: `Your requirements text is too long`,
                          },
                        })}
                      />
                    </div>

                    <div className='col-span-3 sm:col-span-2'>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Attach PDF document (optional)
                      </label>

                      {/* Show the newly uploaded PDF if available */}
                      {tempFileUrl && (
                        <div className='mt-4 mb-4 max-w-full'>
                          <div className='group rounded-lg'>
                            <PdfZoom
                              src={tempFileUrl}
                              border={false}
                              className='w-full'
                              iframeHeight='500px'
                            />
                            <div className='flex justify-end py-1 px-2 bg-gray-50'>
                              <div className='flex space-x-2'>
                                <DownloadFileButton
                                  fileUrl={tempFileUrl}
                                  fileName={
                                    selectedFile?.name || 'document.pdf'
                                  }
                                />
                                <button
                                  onClick={() =>
                                    window.open(tempFileUrl, '_blank')
                                  }
                                  type='button'
                                  className='flex items-center text-gray-400 hover:text-gray-600 focus:outline-none rounded-full bg-white p-1 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200'
                                >
                                  <Icon
                                    type={IconType.externalLink}
                                    className='h-4 w-4 text-primary'
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Show existing PDF if available and no new file is selected */}
                      {!tempFileUrl && hasPdfToShow && pdfUrl && (
                        <div className='mt-4 mb-4 max-w-full'>
                          <div className='group rounded-lg'>
                            <PdfZoom
                              src={pdfUrl}
                              border={false}
                              className='w-full'
                              iframeHeight='500px'
                            />
                            <div className='flex justify-end py-1 px-2 bg-gray-50'>
                              <div className='flex space-x-2'>
                                <DownloadFileButton
                                  fileUrl={pdfUrl}
                                  fileName={extractUuidFromFileName(
                                    campaign.requirementsName,
                                  )}
                                />
                                <button
                                  onClick={() => window.open(pdfUrl, '_blank')}
                                  type='button'
                                  className='flex items-center text-gray-400 hover:text-gray-600 focus:outline-none rounded-full bg-white p-1 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200'
                                >
                                  <Icon
                                    type={IconType.externalLink}
                                    className='h-4 w-4 text-primary'
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {!tempFileUrl && hasPdfToShow && !pdfUrl && (
                        <div className='p-4 bg-gray-50 border border-gray-200 rounded text-gray-500 mb-4'>
                          PDF file is available but the URL is not loaded yet.
                        </div>
                      )}

                      <div>
                        <FileUpload
                          fileName={selectedFile?.name || ''}
                          onChange={handleFileChange}
                        />
                        {selectedFile && !tempFileUrl && (
                          <div className='mt-2 text-sm text-gray-500'>
                            The new PDF will replace the existing one after
                            pressing the "Continue" button
                          </div>
                        )}
                        {isUploading && (
                          <div className='mt-2 text-sm text-gray-500'>
                            Uploading: {uploadProgress}%
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 sm:px-6'>
                <Button
                  title='Go back'
                  type='button'
                  colour={ButtonColor.white}
                  loading={loadingUpdateBrand}
                  disabled={loadingUpdateBrand}
                  onClick={goBack}
                />
                <Button
                  title={
                    isUploading ? `Uploading... ${uploadProgress}%` : 'Continue'
                  }
                  type='submit'
                  loading={
                    loadingUpdateBrand || loadingUpdateCampaign || isUploading
                  }
                  disabled={
                    loadingUpdateBrand || loadingUpdateCampaign || isUploading
                  }
                  loadingPercentage={isUploading ? uploadProgress : undefined}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={BrandOnboardingStep.setRequirements} />
    </div>
  )
}

export { SetRequirements }
